import { Injectable, OnDestroy } from '@angular/core';
import { createClient, SupabaseClient, Session, User } from '@supabase/supabase-js';
import { environment } from '../../environments/environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';

export type UserRole = 'superadmin' | 'admin' | 'contributor' | 'student';

export interface Profile {
  id?: string;
  name?: string;
  full_name?: string | null;
  fam_name?: string;
  email: string;
  phone?: string;
  country?: string;
  city?: string;
  birth_date?: string;
  gender?: string;
  avatar_url?: string | null;
  study_year?: string;
  role?: UserRole;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ModuleItem {
  id?: string;
  study_year?: string | null;
  title: string;
  description?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface NoteItem {
  id?: string;
  module_id: string;
  title: string;
  content?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  is_published?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DocumentItem {
  id?: string;
  title: string;
  file_url: string;
  file_type: 'image' | 'video' | 'audio' | 'document';
  size_bytes?: number;
  bucket_id?: string;
  uploaded_by?: string | null;
  created_at?: string;
}

class CapacitorStorageAdapter {
  async getItem(key: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key });
      return value ?? null;
    } catch (e) {
      console.warn('[Storage] getItem failed', e);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      await Preferences.set({ key, value });
    } catch (e) {
      console.warn('[Storage] setItem failed', e);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch (e) {
      console.warn('[Storage] removeItem failed', e);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class SupabaseService implements OnDestroy {
  private supabase: SupabaseClient;

  private _currentUser = new BehaviorSubject<User | null>(null);
  public currentUser$ = this._currentUser.asObservable();

  private _session = new BehaviorSubject<Session | null>(null);
  public session$ = this._session.asObservable();

  private initialized = false;
  private initializationPromise: Promise<void> | null = null;

  public parcoursList: any[] = [];

  constructor(
    private router: Router,
    private platform: Platform
  ) {
    this.supabase = createClient(environment.supabaseUrl, environment.supabaseKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        flowType: 'pkce',
        storage: new CapacitorStorageAdapter() as any,
      },
    });

    this.supabase.auth.onAuthStateChange((event, session) => {
      this._session.next(session ?? null);
      this._currentUser.next(session?.user ?? null);

      if (event === 'SIGNED_OUT') {
        this.parcoursList = [];
      }
    });

    this.initializationPromise = this.init();
  }
get auth() {
  return this.supabase.auth;
}
  private async init(): Promise<void> {
    if (this.initialized) return;
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      this._session.next(session ?? null);
      this._currentUser.next(session?.user ?? null);
    } catch (e) {
      console.warn('[Init] getSession failed', e);
      this._session.next(null);
      this._currentUser.next(null);
    } finally {
      this.initialized = true;
      this.initializationPromise = null;
    }
  }

  public async waitForInitialization(): Promise<void> {
    if (this.initialized) return;
    if (!this.initializationPromise) this.initializationPromise = this.init();
    return this.initializationPromise!;
  }
waitForResume() {
  return Promise.resolve();
}

  public get client(): SupabaseClient {
    return this.supabase;
  }

  public getCurrentUserSync(): User | null {
    return this._currentUser.value;
  }

  public async getCurrentUser(): Promise<User | null> {
    await this.waitForInitialization();
    return this._currentUser.value;
  }

  public async getUser(): Promise<User | null> {
    return this.getCurrentUser();
  }

  public async getUserRole(): Promise<UserRole | null> {
    const user = await this.getCurrentUser();
    if (!user) return null;

    const { data, error } = await this.supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle();

    if (error && (error as any).code !== 'PGRST116') {
      throw error;
    }

    const profileRole = this.normalizeRole(data?.role);
    if (profileRole) return profileRole;

    const metadataRole = this.normalizeRole(user.user_metadata?.['role'] ?? user.user_metadata?.['role']);
    if (metadataRole) return metadataRole;

    return 'student';
  }

  private normalizeRole(role: unknown): UserRole | null {
    if (typeof role !== 'string') return null;

    const normalized = role.toLowerCase();
    if (normalized === 'superadmin' || normalized === 'admin' || normalized === 'contributor' || normalized === 'student') {
      return normalized as UserRole;
    }

    return null;
  }

  public isSuperAdminRole(role?: string | null): boolean {
    return role === 'superadmin';
  }

  public isAdminRole(role?: string | null): boolean {
    return role === 'superadmin' || role === 'admin';
  }

  public isContributorRole(role?: string | null): boolean {
    return role === 'superadmin' || role === 'admin' || role === 'contributor';
  }

  public async isAuthenticated(): Promise<boolean> {
    await this.waitForInitialization();
    return this._currentUser.value !== null;
  }

  public async createUser(input: {
    email: string;
    password: string;
    full_name?: string;
    role?: UserRole;
    avatar_url?: string | null;
  }): Promise<User | null> {
    const { data, error } = await this.supabase.functions.invoke('create-admin-user', {
      body: input,
    });

    if (error) {
      const response = (error as any).context as Response | undefined;
      if (response && typeof (response as any).clone === 'function') {
        try {
          const body = await response.clone().json();
          if (typeof body?.error === 'string') {
            throw new Error(body.error);
          }
        } catch (parseError) {
          if (parseError instanceof Error && parseError.message !== 'Unexpected end of JSON input') {
            throw parseError;
          }
        }
      }
      throw error;
    }

    return data?.user ?? null;
  }

  public async updateManagedUser(input: {
    id: string;
    full_name: string;
    email: string;
    password?: string;
    role: UserRole;
    is_active: boolean;
  }): Promise<void> {
    await this.invokeUserManagementFunction({ action: 'update', ...input });
  }

  public async deleteManagedUser(userId: string): Promise<void> {
    await this.invokeUserManagementFunction({ action: 'delete', userId });
  }

  private async invokeUserManagementFunction(body: Record<string, unknown>): Promise<void> {
    const { error } = await this.supabase.functions.invoke('manage-admin-user', { body });

    if (!error) return;

    const response = (error as any).context as Response | undefined;
    if (response && typeof (response as any).clone === 'function') {
      try {
        const responseBody = await response.clone().json();
        if (typeof responseBody?.error === 'string') {
          throw new Error(responseBody.error);
        }
      } catch (parseError) {
        if (parseError instanceof Error && parseError.message !== 'Unexpected end of JSON input') {
          throw parseError;
        }
      }
    }

    throw error;
  }

  public async updateUser(userId: string, updates: Partial<Profile>): Promise<void> {
    const { error } = await this.supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) throw error;
  }

  public async assignRole(userId: string, role: UserRole): Promise<void> {
    const { error } = await this.supabase
      .from('profiles')
      .update({ role, updated_at: new Date().toISOString() })
      .eq('id', userId);

    if (error) throw error;
  }

  public async createModule(input: Omit<ModuleItem, 'id' | 'created_at' | 'updated_at'>): Promise<ModuleItem> {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('No user logged in.');

    const { data, error } = await this.supabase
      .from('modules')
      .insert([
        {
          ...input,
          created_by: user.id,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as ModuleItem;
  }

  public async createNote(input: Omit<NoteItem, 'id' | 'created_by' | 'updated_by' | 'created_at' | 'updated_at'>): Promise<NoteItem> {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('No user logged in.');

    const { data, error } = await this.supabase
      .from('notes')
      .insert([
        {
          ...input,
          created_by: user.id,
          updated_by: user.id,
          is_published: input.is_published ?? false,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as NoteItem;
  }

  public  async uploadDocument(input: {
    file: File;
    fileType: DocumentItem['file_type'];
    bucket: 'images' | 'videos' | 'audio' | 'documents';
  }): Promise<DocumentItem> {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('No user logged in.');

    const fileExt = input.file.name.split('.').pop() ?? 'file';
    const path = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;

    const { error: uploadError } = await this.supabase.storage
      .from(input.bucket)
      .upload(path, input.file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: publicUrlData } = this.supabase.storage
      .from(input.bucket)
      .getPublicUrl(path);

    const { data, error } = await this.supabase
      .from('documents')
      .insert([
        {
          title: input.file.name,
          file_url: publicUrlData.publicUrl,
          file_type: input.fileType,
          size_bytes: input.file.size,
          bucket_id: input.bucket,
          uploaded_by: user.id,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as DocumentItem;
  }

  public async getDashboardStats() {
    const [users, modules, notes, qcms, questions, attempts] = await Promise.all([
      this.supabase.from('profiles').select('*', { count: 'exact', head: true }),
      this.supabase.from('modules').select('*', { count: 'exact', head: true }),
      this.supabase.from('notes').select('*', { count: 'exact', head: true }),
      this.supabase.from('qcms').select('*', { count: 'exact', head: true }),
      this.supabase.from('questions').select('*', { count: 'exact', head: true }),
      this.supabase.from('user_qcms').select('*', { count: 'exact', head: true }),
    ]);

    const { data: studyYearsData } = await this.supabase
      .from('profiles')
      .select('study_year');

    const { data: avgScoreData } = await this.supabase
      .from('user_qcms')
      .select('score, max_score')
      .not('finished_at', 'is', null);

    return {
      users: users.count ?? 0,
      modules: modules.count ?? 0,
      notes: notes.count ?? 0,
      qcms: qcms.count ?? 0,
      questions: questions.count ?? 0,
      attempts: attempts.count ?? 0,
      studyYearsData: studyYearsData ?? [],
      avgScoreData: avgScoreData ?? [],
    };
  }


public async signUp(
  email: string,
  password: string,
  profileData?: Partial<Profile>
) {

  try {
    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: {
          name: profileData?.name ?? '',
          fam_name: profileData?.fam_name ?? '',
          phone: profileData?.phone ?? '',
          city: profileData?.city ?? '',
          study_year: profileData?.study_year ?? '',
          role: profileData?.role ?? 'student',
        },
      },
    });

    if (error) {

      console.error("🔥 Supabase error:", error);

      const normalizedError = this.parseSupabaseSignupError(error);
      throw normalizedError;
    }

    return data;

  } catch (e: any) {
    console.error("🔥 Signup catch:", e);
    throw e;
  }
}



private parseSupabaseSignupError(error: any) {
  const message = error.message?.toLowerCase() || "";
  const status = error.status || 400;

  if (message.includes("email") && message.includes("exists"))
    return { code: "EMAIL_EXISTS", message: "Cet email est déjà utilisé." };

  if (message.includes("invalid email") || message.includes("email address is invalid"))
    return { code: "INVALID_EMAIL", message: "Email invalide." };

  if (message.includes("password") && message.includes("weak"))
    return { code: "WEAK_PASSWORD", message: "Mot de passe faible." };

  if (message.includes("password should"))
    return { code: "WEAK_PASSWORD", message: "Mot de passe trop faible." };

  if (status === 429 || message.includes("rate limit"))
    return {
      code: "RATE_LIMIT",
      message: "Trop de tentatives. Veuillez réessayer plus tard."
    };

  return {
    code: "UNKNOWN",
    message: "Une erreur inattendue s’est produite. Réessayez."
  };
}


  public async signIn(email: string, password: string) {
    const { data, error } = await this.supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) {
      const { data: profile, error: profileError } = await this.supabase
        .from('profiles')
        .select('is_active')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError) {
        await this.supabase.auth.signOut();
        throw new Error('Unable to verify whether this account is active. Please contact an administrator.');
      }

      if (profile?.is_active === false) {
        await this.supabase.auth.signOut();
        throw new Error('This account has been deactivated. Please contact an administrator.');
      }

      void this.logCurrentUserActivity('signed_in', 'session');
    }
    return data;
  }

  public async signOut(redirectToLogin = true): Promise<void> {
    try {
      await this.logCurrentUserActivity('signed_out', 'session');
      await this.supabase.auth.signOut();
      this._session.next(null);
      this._currentUser.next(null);
      this.parcoursList = [];
      if (redirectToLogin) {
        await this.router.navigateByUrl('/login', { replaceUrl: true });
      }
    } catch (e) {
      console.error('[SignOut] Error during sign out', e);
      this._session.next(null);
      this._currentUser.next(null);
      this.parcoursList = [];
      if (redirectToLogin) {
        await this.router.navigateByUrl('/login', { replaceUrl: true });
      }
    }
  }

  private async logCurrentUserActivity(action: string, entityType: string): Promise<void> {
    try {
      const { error } = await this.supabase.rpc('log_current_user_activity', {
        p_action: action,
        p_entity_type: entityType,
      });
      if (error) console.warn('Unable to record user activity:', error.message);
    } catch (error) {
      // Logging must never prevent a user from signing in or out.
      console.warn('Unable to record user activity:', error);
    }
  }

  public async resetPassword(email: string, redirectTo?: string) {
   
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo ?? window.location.origin + '/reset-password',
    });
    if (error) throw error;
    return true;
  }

  public async verifyOtp(email: string, token: string) {
    const { data, error } = await this.supabase.auth.verifyOtp({
      email,
      token,
      type: 'email'
    });
    if (error) throw error;
    return data;
  }

  public async resendOtp(email: string) {
    const { error } = await this.supabase.auth.resend({
      type: 'signup',
      email
    });
    if (error) throw error;
    return true;
  }

  public async updatePassword(newPassword: string) {
    const { data, error } = await this.supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  }

  public async ensureUserProfileExists(): Promise<void> {
    const user = await this.getCurrentUser();
    if (!user) return;

    const { data: existingProfile, error } = await this.supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (error) throw error;

    if (!existingProfile) {
      await this.createUserProfile(user.id, {
        name: user.user_metadata?.['name'] ?? '',
        fam_name: user.user_metadata?.['fam_name'] ?? '',
        email: user.email ?? '',
        phone: user.user_metadata?.['phone'] ?? '',
        city: user.user_metadata?.['city'] ?? '',
        avatar_url: null,
        study_year: user.user_metadata?.['study_year'] ?? '',
        role: this.normalizeRole(user.user_metadata?.['role'] ?? user.user_metadata?.['role']) ?? 'student',
      });
    }
  }

  public async createUserProfile(userId: string, profileData: Profile): Promise<void> {
    const { error } = await this.supabase.from('profiles').insert([
      {
        id: userId,
        name: profileData.name,
        fam_name: profileData.fam_name,
        email: profileData.email,
        phone: profileData.phone,
        study_year: profileData.study_year,
        city: profileData.city,
        birth_date: profileData.birth_date,
        gender: profileData.gender,
        avatar_url: profileData.avatar_url ?? null,
        role: profileData.role ?? 'student',
      },
    ]);

    if (error) throw error;

    if (profileData.study_year) {
      const { error: accessError } = await this.supabase.from('user_year_access').insert([
        {
          user_id: userId,
          study_year: profileData.study_year,
          is_active: true,
        },
      ]);

      if (accessError && (accessError as any).code !== '23505') throw accessError;
    }
  }

  public async getProfileById(userId: string) {
    const { data, error } = await this.supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
    if (error) throw error;
    return data;
  }

  public async getProfile() {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('Utilisateur non connecté.');

    const { data, error } = await this.supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
    if (error) throw error;

    if (!data) {
      await this.createUserProfile(user.id, {
        name: user.user_metadata?.['name'] ?? '',
        email: user.email ?? '',
        study_year: user.user_metadata?.['study_year'] ?? '',
      } as Profile);

      return { id: user.id, email: user.email, name: user.user_metadata?.['name'] ?? '' };
    }

    return { ...data, email: user.email };
  }

  public async updateProfile(profileData: Partial<Profile>) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('No user logged in.');

    const updates = { ...profileData, updated_at: new Date() } as any;
    const { error } = await this.supabase.from('profiles').update(updates).eq('id', user.id);
    if (error) throw error;

    if (profileData.study_year) {
      await this.grantYearAccess(user.id, profileData.study_year);
    }
  }

  public async getUserYearAccess(userId?: string) {
    const user = userId ?? (await this.getCurrentUser())?.id;
    if (!user) throw new Error('No user logged in.');

    const { data, error } = await this.supabase
      .from('user_year_access')
      .select('study_year, is_active')
      .eq('user_id', user)
      .eq('is_active', true);

    if (error) throw error;
    return data ?? [];
  }

  public async grantYearAccess(userId: string, studyYear: string) {
  // Start a transaction to ensure atomicity
  const { data: existingYear, error: checkError } = await this.supabase
    .from('user_year_access')
    .select('id, is_active')
    .eq('user_id', userId)
    .eq('study_year', studyYear)
    .single();

  // If the year already exists
  if (existingYear) {
    // If it's already active, nothing to do
    if (existingYear.is_active) {
      return { data: existingYear, error: null };
    }

    // Deactivate all other years for this user
    const { error: deactivateError } = await this.supabase
      .from('user_year_access')
      .update({ is_active: false })
      .eq('user_id', userId)
      .neq('study_year', studyYear);

    if (deactivateError) throw deactivateError;

    // Activate this year
    const { data, error } = await this.supabase
      .from('user_year_access')
      .update({ is_active: true })
      .eq('id', existingYear.id)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  }

  // Year doesn't exist, so we need to create it
  // First, deactivate all other years
  const { error: deactivateError } = await this.supabase
    .from('user_year_access')
    .update({ is_active: false })
    .eq('user_id', userId);

  if (deactivateError) throw deactivateError;

  // Insert the new year as active
  const { data, error } = await this.supabase
    .from('user_year_access')
    .insert([
      {
        user_id: userId,
        study_year: studyYear,
        is_active: true,
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return { data, error: null };
}

// Helper method to get the active year for a user
public async getActiveYear(userId: string) {
  const { data, error } = await this.supabase
    .from('user_year_access')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .single();

  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
  return data;
}

public async getAllUserYears(userId: string) {
  const { data, error } = await this.supabase
    .from('user_year_access')
    .select('*')
    .eq('user_id', userId)
    .order('granted_at', { ascending: false });

  if (error) throw error;
  return data;
}

  public async loadModulesByStudyYear(studyYear: string) {
    const user = await this.getCurrentUser();
    if (!user) return [];

    const { data: accessData, error: accessError } = await this.supabase
      .from('user_year_access')
      .select('is_active')
      .eq('user_id', user.id)
      .eq('study_year', studyYear)
      .eq('is_active', true)
      .maybeSingle();

    if (accessError) throw accessError;
    if (!accessData) return [];

    const { data, error } = await this.supabase
      .from('modules')
      .select(`*, user_modules!left(status, completion_rate, average_qcm_score)`)
      .eq('study_year', studyYear)
      .eq('user_modules.user_id', user.id)
      .order('position', { ascending: true });

    if (error) throw error;
    return data ?? [];
  }

  public async updateModuleActivity(userId: string, moduleId: string) {
    const { error } = await this.supabase
      .from('user_modules')
      .update({ last_activity_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('module_id', moduleId);

    if (error) throw error;
  }

  public async startModule(userId: string, moduleId: string) {
    const now = new Date().toISOString();
    const { error } = await this.supabase.from('user_modules').insert({
      user_id: userId,
      module_id: moduleId,
      status: 'en cours',
      completion_rate: 0,
      started_at: now,
      last_activity_at: now,
    });

    if (error) {
      if (!String(error.message).toLowerCase().includes('duplicate')) throw error;
    }
  }

  public async uploadAvatar(file: File): Promise<string> {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('No user logged in.');

    const fileExt = file.name.split('.').pop();
    const filePath = `avatars/${user.id}/${Date.now()}.${fileExt}`;

    const { error } = await this.supabase.storage.from('avatars').upload(filePath, file, { upsert: true });
    if (error) throw error;

    const { data } = this.supabase.storage.from('avatars').getPublicUrl(filePath);
    return data.publicUrl;
  }

  public async loadParcours() {
    const user = await this.getCurrentUser();
    if (!user) return [];

    const { data, error } = await this.supabase
      .from('parcours')
      .select(`*, user_parcours!left(status, progression_rate)`)
      .eq('user_parcours.user_id', user.id);

    if (error) throw error;

    this.parcoursList = data ?? [];
    return this.parcoursList;
  }

  public async startParcours(parcoursId: string) {
    const user = await this.getCurrentUser();
    if (!user) throw new Error('Utilisateur non connecté.');

    const now = new Date();
    const { error } = await this.supabase.from('user_parcours').insert({
      user_id: user.id,
      parcours_id: parcoursId,
      status: 'in_progress',
      progression_rate: 0,
      date_started: now,
    });

    if (error) {
      if (!String(error.message).toLowerCase().includes('duplicate')) throw error;
    }

    const parcours = this.parcoursList.find((p: any) => p.id === parcoursId);
    if (parcours) parcours.user_parcours = [{ status: 'in_progress', progression_rate: 0 }];

    await this.router.navigate(['/modules', parcoursId]);
  }

  public async refreshSession(): Promise<boolean> {
    try {
      const { data, error } = await this.supabase.auth.refreshSession();
      if (error) {
        console.warn('[Refresh] refreshSession returned error', error);
        return false;
      }
      if (data?.session) {
        this._session.next(data.session);
        this._currentUser.next(data.session.user ?? null);
        return true;
      }
      return false;
    } catch (e) {
      console.warn('[Refresh] exception', e);
      return false;
    }
  }

  // ============================================
  // PAGINATION METHODS
  // ============================================

  /**
   * Load QCMs for a module with pagination
   * @param moduleId Module ID
   * @param page Page number (starts from 0)
   * @param pageSize Number of items per page (default: 10)
   * @returns Object with data, total count, and pagination info
   */
  public async loadQcmsWithPagination(moduleId: string, page: number = 0, pageSize: number = 10) {
    const user = await this.getCurrentUser();
    if (!user) return { data: [], totalCount: 0, hasMore: false };

    const from = page * pageSize;
    const to = from + pageSize - 1;

    try {
      // Get total count
      const { count: totalCount } = await this.supabase
        .from('qcms')
        .select('*', { count: 'exact', head: true })
        .eq('module_id', moduleId);

      // Get paginated data
      const { data, error } = await this.supabase
        .from('qcms')
        .select('*')
        .eq('module_id', moduleId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        data: data || [],
        totalCount: totalCount || 0,
        currentPage: page,
        pageSize,
        hasMore: (totalCount || 0) > to + 1
      };
    } catch (err) {
      console.error('Error loading QCMs with pagination:', err);
      return { data: [], totalCount: 0, hasMore: false };
    }
  }

  /**
   * Load QCM attempts history with pagination
   * @param qcmId QCM ID
   * @param userId User ID
   * @param page Page number (starts from 0)
   * @param pageSize Number of items per page (default: 20)
   * @returns Object with data, total count, and pagination info
   */
  public async loadQcmAttemptsWithPagination(
    qcmId: string,
    userId: string,
    page: number = 0,
    pageSize: number = 20
  ) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    try {
      // Get total count
      const { count: totalCount } = await this.supabase
        .from('user_qcms')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('qcm_id', qcmId)
        .not('finished_at', 'is', null);

      // Get paginated data
      const { data, error } = await this.supabase
        .from('user_qcms')
        .select('*')
        .eq('user_id', userId)
        .eq('qcm_id', qcmId)
        .not('finished_at', 'is', null)
        .order('finished_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        data: data || [],
        totalCount: totalCount || 0,
        currentPage: page,
        pageSize,
        hasMore: (totalCount || 0) > to + 1
      };
    } catch (err) {
      console.error('Error loading QCM attempts with pagination:', err);
      return { data: [], totalCount: 0, hasMore: false };
    }
  }

  /**
   * Load questions for a QCM with pagination
   * @param qcmId QCM ID
   * @param page Page number (starts from 0)
   * @param pageSize Number of items per page (default: 10)
   * @returns Object with data, total count, and pagination info
   */
  public async loadQuestionsWithPagination(qcmId: string, page: number = 0, pageSize: number = 10) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    try {
      // Get total count
      const { count: totalCount } = await this.supabase
        .from('questions')
        .select('*', { count: 'exact', head: true })
        .eq('qcm_id', qcmId);

      // Get paginated data with options
      const { data, error } = await this.supabase
        .from('questions')
        .select(`
          *,
          qst_options (
            id,
            answer_text,
            is_correct,
            order_index
          )
        `)
        .eq('qcm_id', qcmId)
        .order('order_index', { ascending: true })
        .range(from, to);

      if (error) throw error;

      return {
        data: data || [],
        totalCount: totalCount || 0,
        currentPage: page,
        pageSize,
        hasMore: (totalCount || 0) > to + 1
      };
    } catch (err) {
      console.error('Error loading questions with pagination:', err);
      return { data: [], totalCount: 0, hasMore: false };
    }
  }

  /**
   * Load modules with pagination
   * @param studyYear Study year
   * @param page Page number (starts from 0)
   * @param pageSize Number of items per page (default: 10)
   * @returns Object with data, total count, and pagination info
   */
  public async loadModulesWithPagination(studyYear: string, page: number = 0, pageSize: number = 10) {
    const user = await this.getCurrentUser();
    if (!user) return { data: [], totalCount: 0, hasMore: false };

    const from = page * pageSize;
    const to = from + pageSize - 1;

    try {
      // Check year access
      const { data: accessData } = await this.supabase
        .from('user_year_access')
        .select('is_active')
        .eq('user_id', user.id)
        .eq('study_year', studyYear)
        .eq('is_active', true)
        .maybeSingle();

      if (!accessData) return { data: [], totalCount: 0, hasMore: false };

      // Get total count
      const { count: totalCount } = await this.supabase
        .from('modules')
        .select('id', { count: 'exact', head: true })
        .eq('study_year', studyYear);

      // Get paginated data
      const { data, error } = await this.supabase
        .from('modules')
        .select(`*, user_modules!left(status, completion_rate, average_qcm_score)`)
        .eq('study_year', studyYear)
        .eq('user_modules.user_id', user.id)
        .order('position', { ascending: true })
        .range(from, to);

      if (error) throw error;

      return {
        data: data || [],
        totalCount: totalCount || 0,
        currentPage: page,
        pageSize,
        hasMore: (totalCount || 0) > to + 1
      };
    } catch (err) {
      console.error('Error loading modules with pagination:', err);
      return { data: [], totalCount: 0, hasMore: false };
    }
  }

  /**
   * Load notes for a module with pagination
   * @param moduleId Module ID
   * @param page Page number (starts from 0)
   * @param pageSize Number of items per page (default: 10)
   * @returns Object with data, total count, and pagination info
   */
  public async loadNotesWithPagination(moduleId: string, page: number = 0, pageSize: number = 10) {
    const user = await this.getCurrentUser();
    if (!user) return { data: [], totalCount: 0, hasMore: false };

    const from = page * pageSize;
    const to = from + pageSize - 1;

    try {
      // Get total count
      const { count: totalCount } = await this.supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('module_id', moduleId);

      // Get paginated data
      const { data, error } = await this.supabase
        .from('notes')
        .select('*, user_notes(user_id, status, completed_at)')
        .eq('module_id', moduleId)
        .eq('user_notes.user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        data: data || [],
        totalCount: totalCount || 0,
        currentPage: page,
        pageSize,
        hasMore: (totalCount || 0) > to + 1
      };
    } catch (err) {
      console.error('Error loading notes with pagination:', err);
      return { data: [], totalCount: 0, hasMore: false };
    }
  }

  ngOnDestroy(): void {
     }
}
